import moment from "moment";
import { doesOrderContainShortWeek, getOrderAdjustments, } from "../../../Shared/components/OrderSummary/orderSummary-utils";
import { useOrderFlow } from "../../OrderFlowProvider/OrderFlowContext";
import { getDeliveryDatesForOrder, getProductsForOrderPeriod, } from "../../hooks/Session/sessionUtils";
import { getSelectedIngredientOptionTotal } from "../../hooks/useCalculations";
import { getSubscriptionMeals } from "../../utils/orderFlowUtils";
import { getDatesForCart } from "./useCartDrawerUtils";
/**
 * useCart - Handles pricing and meal breakdown and coupon submission for the cart
 * @param pricingBreakdown
 * @returns *
 */
export const useCartDrawer = (balance) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
    const { store } = useOrderFlow();
    const subscriptionMeals = getSubscriptionMeals(store.cart, store.model);
    const daysOfWeek = [...new Set(subscriptionMeals.map((sm) => sm.DayOfWeek))];
    const subscriptionMealsToDisplay = [];
    const deliveryDaysForOrder = getDeliveryDatesForOrder(store.cart.meals, store.cart.products.filter((p) => p.quantity > 0), store.model.DeliveryDays, moment(store.cart.startDate).format())
        .map((dd) => moment(dd).day())
        .sort();
    const productsToDisplay = getProductsForOrderPeriod(moment(store.cart.startDate).day(), deliveryDaysForOrder, store.cart.products.filter((p) => p.quantity > 0), store.model.Products);
    daysOfWeek.forEach((dayOfWeek) => {
        const targetMealsForDay = subscriptionMeals.filter((subMeal) => subMeal.DayOfWeek === dayOfWeek);
        subscriptionMealsToDisplay.push({
            day: dayOfWeek,
            meals: targetMealsForDay.map((meal) => {
                var _a;
                return {
                    name: meal.MealName,
                    quantity: meal.Qty,
                    price: (_a = meal.Price) !== null && _a !== void 0 ? _a : 0,
                };
            }),
        });
    });
    //Sort meals by date inc. and remove duplicates to create string array of dates
    const mealDates = [
        ...new Set((_b = (_a = store.cart.meals) === null || _a === void 0 ? void 0 : _a.filter((meal) => !meal.IsSkipped)) === null || _b === void 0 ? void 0 : _b.map((meal) => moment(meal.MealDate)).sort((a, b) => a.diff(b)).filter((item, pos, arr) => {
            return !pos || !moment(item).isSame(arr[pos - 1], "date");
        })),
    ];
    // Flatten the Store/Order Flow model into an OrderSummary
    const orderSummary = {
        planName: store.model.Program.Name,
        sku: (_c = store.model.Sku) !== null && _c !== void 0 ? _c : store.model.Program.Sku,
        startDate: moment(store.cart.startDate).format("YYYY-MM-DD"),
        isSubscription: store.cart.isSubscription,
        subscriptionPeriod: store.cart.subscriptionPeriod,
        omissions: (_d = store.cart.omissions) === null || _d === void 0 ? void 0 : _d.map((omission) => ({
            name: omission.dislikeName,
            id: omission.dislikeId,
        })),
        recipeTypeName: (_e = store.cart.recipeType) === null || _e === void 0 ? void 0 : _e.recipeTypeName,
        mealSize: store.cart.mealSize,
        postalCode: store.model.PostalCode,
        location: (_f = store.model.Location) === null || _f === void 0 ? void 0 : _f.name,
        totalDayBankValue: 0,
        appliedDayBanks: [],
        dates: getDatesForCart(mealDates, store.cart.meals, productsToDisplay, store, getSelectedIngredientOptionTotal),
        subscriptions: {
            products: (_g = store.cart.products) === null || _g === void 0 ? void 0 : _g.filter((product) => product.quantity > 0 && product.isRenewing).map((product) => {
                var _a;
                return {
                    name: product.productName,
                    quantity: product.quantity,
                    id: product.productId,
                    price: product.productPrice,
                    sale: product.salePrice !== 0 ? product.salePrice : undefined,
                    dayOfWeek: (_a = product.DayOfWeek) !== null && _a !== void 0 ? _a : -1,
                };
            }),
            meals: subscriptionMealsToDisplay,
        },
        shortWeekProducts: productsToDisplay.filter(p => p.isOnShortWeek).map((product) => {
            var _a;
            return {
                name: product.productName,
                quantity: product.quantity,
                id: product.productId,
                price: product.productPrice,
                sale: product.salePrice !== 0 ? product.salePrice : undefined,
                dayOfWeek: (_a = product.DayOfWeek) !== null && _a !== void 0 ? _a : -1,
                isOnShortWeek: product.isOnShortWeek,
            };
        }),
        discounts: {
            subscription: (balance === null || balance === void 0 ? void 0 : balance.cost.order.adjustmentBreakdown.totalSubscriptionDiscount) &&
                (balance === null || balance === void 0 ? void 0 : balance.cost.order.adjustmentBreakdown.totalSubscriptionDiscount) > 0
                ? {
                    value: store.model.subscriptionDiscount,
                    appliedValue: balance === null || balance === void 0 ? void 0 : balance.cost.order.adjustmentBreakdown.totalSubscriptionDiscount,
                }
                : undefined,
            coupons: getOrderAdjustments(balance),
            giftCard: (balance === null || balance === void 0 ? void 0 : balance.giftCardPayments) && (balance === null || balance === void 0 ? void 0 : balance.giftCardPayments.length) > 0
                ? {
                    giftCardAmount: balance === null || balance === void 0 ? void 0 : balance.giftCardPayments[0].appliedValue,
                    giftCardNumber: balance === null || balance === void 0 ? void 0 : balance.giftCardPayments[0].giftCardCode,
                }
                : undefined,
        },
        questions: (_h = store.cart.questionAnswers) === null || _h === void 0 ? void 0 : _h.filter((q) => q.Answer !== undefined && q.Answer !== "" && q.seen).map((question) => ({
            sku: question.QuestionSKU,
            answer: question.Answer,
            price: question.UpchargePrice,
        })),
        tax: (_j = balance === null || balance === void 0 ? void 0 : balance.cost.order.totalTax) !== null && _j !== void 0 ? _j : 0,
        taxRate: (_k = balance === null || balance === void 0 ? void 0 : balance.cost.order.taxRate) !== null && _k !== void 0 ? _k : 0,
        shipping: (_l = balance === null || balance === void 0 ? void 0 : balance.cost.order.totalShipping) !== null && _l !== void 0 ? _l : 0,
        subtotal: (_m = balance === null || balance === void 0 ? void 0 : balance.cost.order.subtotal) !== null && _m !== void 0 ? _m : 0,
        total: (_o = balance === null || balance === void 0 ? void 0 : balance.remainingBalance) !== null && _o !== void 0 ? _o : 0,
        fullDayDiscount: (_p = balance === null || balance === void 0 ? void 0 : balance.cost.order.adjustmentBreakdown.totalFullDayDiscount) !== null && _p !== void 0 ? _p : 0,
        containsShortWeek: store.cart.isSubscription && doesOrderContainShortWeek(store.cart.meals, subscriptionMeals),
        isFutureOrder: false,
    };
    return {
        orderSummary,
    };
};
